// components/UIUX.js
import React, { useState, useEffect, useCallback } from 'react';

const UIUX = () => {
  const [activeProject, setActiveProject] = useState('bitebuddy');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const uiuxProjects = {
    bitebuddy: {
      id: 'bitebuddy',
      title: 'Bite Buddy - Online Food Order App',
      description: 'A comprehensive food ordering platform with intuitive user interface and seamless user experience designed in Figma. Features include smart search, personalized recommendations, and smooth checkout flow.',
      images: Array.from({ length: 14 }, (_, i) => `uiux/bitebuddy/${String(i + 1).padStart(2, '0')}.png`),
      category: 'Mobile App',
      platform: 'iOS & Android',
      features: [
        'User-friendly onboarding process',
        'Intuitive menu browsing',
        'Smart search with filters',
        'Personalized recommendations',
        'Smooth checkout experience',
        'Order tracking in real-time',
        'Ratings and reviews system'
      ],
      tools: ['Figma', 'Adobe Illustrator', 'Canva'],
      colors: ['#EEF5FC', '#F8BB71', '#EA8E31', '#FB5021', '#FFFFFF'],
      prototypeLink: 'https://www.figma.com/proto/bHOuvI0h8K6F91uDZMAwG7/Bite-Buddy-%E2%80%93-Food-App-UI-UX?node-id=3-7&p=f&t=pxuYR7ea11GFzenr-1&scaling=scale-down&content-scaling=fixed&page-id=2%3A2058&starting-point-node-id=3%3A7'

    },
    scss: {
      id: 'scss',
      title: 'Smart City Services System (SCSS)',
      description: 'A digital platform for urban services management with clean, modern interface designed for both citizens and administrators. Focuses on accessibility and efficiency.',
      images: Array.from({ length: 7 }, (_, i) => `uiux/scss/${String(i + 1).padStart(2, '0')}.png`),
      category: 'Web Dashboard',
      platform: 'Responsive Web',
      features: [
        'Clean, modern dashboard design',
        'Service request management',
        'Real-time notifications',
        'Analytics and reporting',
        'Multi-role access control',
        'Mobile-responsive layout',
        'Dark/Light mode support'
      ],
      tools: ['Figma', 'Canva', 'Adobe Illustrator'],
      colors: ['#2C76C7', '#3796B9', '#44A8A3', '#51B98D', '#FFFFFF'],
      prototypeLink: 'https://www.figma.com/proto/Q2xQwTfBBlRwAAxcHQ20yl/SCSS?node-id=4-358&p=f&t=69uVsMuDmbLh22Q2-1&scaling=scale-down&content-scaling=fixed&page-id=0%3A1&starting-point-node-id=2%3A2'
    }
  };

  const activeProjectData = uiuxProjects[activeProject];

  const nextImage = useCallback(() => {
    setActiveImageIndex(prev => (prev + 1) % activeProjectData.images.length);
  }, [activeProjectData.images.length]);


  const prevImage = useCallback(() => {
    setActiveImageIndex(prev =>
      (prev - 1 + activeProjectData.images.length) % activeProjectData.images.length
    );
  }, [activeProjectData.images.length]);

  // Reset image index when project changes
  useEffect(() => {
    setActiveImageIndex(0);
  }, [activeProject]);

  // Handle keyboard navigation in fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isFullscreen) return;

      if (e.key === 'Escape') setIsFullscreen(false);
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, nextImage, prevImage]);


  const openFullscreen = (index) => {
    setActiveImageIndex(index);
    setIsFullscreen(true);
  };

  const handleImageError = (e, imagePath) => {
    console.error(`Failed to load image: ${imagePath}`);
    e.target.src = process.env.PUBLIC_URL + '/assets/placeholder-ui.png';
  };

  return (
    <>
      <section id="uiux" className="py-20 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-500">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 dark:text-white mb-4">
              UI/UX <span className="text-purple-600 dark:text-purple-400">Designs</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Where aesthetics meet functionality. Explore my Figma designs that bring ideas to life
              with pixel-perfect precision and user-centered approach.
            </p>
          </div>

          {/* Project Selection */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {Object.values(uiuxProjects).map((project) => (
              <button
                key={project.id}
                onClick={() => setActiveProject(project.id)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${activeProject === project.id
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-xl'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 shadow-lg'
                  }`}
              >
                {project.title.split(' - ')[0]}
              </button>
            ))}
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Main Project Display */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl overflow-hidden mb-12">
              {/* Project Header */}
              <div className="p-8 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white mb-2">
                      {activeProjectData.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 max-w-2xl">
                      {activeProjectData.description}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <span className="px-4 py-2 bg-white dark:bg-gray-800 rounded-full text-sm font-semibold text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800">
                      {activeProjectData.category}
                    </span>
                    <span className="px-4 py-2 bg-white dark:bg-gray-800 rounded-full text-sm font-semibold text-pink-600 dark:text-pink-400 border border-pink-200 dark:border-pink-800">
                      {activeProjectData.platform}
                    </span>
                  </div>
                </div>
              </div>

              {/* Main Image Display */}
              <div className="p-8">
                <div className="relative rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-700 shadow-inner">
                  <div
                    className="cursor-zoom-in aspect-video flex items-center justify-center"
                    onClick={() => openFullscreen(activeImageIndex)}
                  >
                    <img
                      src={process.env.PUBLIC_URL + '/assets/' + activeProjectData.images[activeImageIndex]}
                      alt={`${activeProjectData.title} - Design ${activeImageIndex + 1}`}
                      className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                      onError={(e) => handleImageError(e, activeProjectData.images[activeImageIndex])}
                      loading="lazy"
                    />
                  </div>

                  {/* Image Controls */}
                  {activeProjectData.images.length > 1 && (
                    <div className="absolute bottom-4 right-4 flex gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); prevImage(); }}
                        className="bg-black/80 hover:bg-black text-white p-3 rounded-full transition-all hover:scale-110"
                        aria-label="Previous design"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); nextImage(); }}
                        className="bg-black/80 hover:bg-black text-white p-3 rounded-full transition-all hover:scale-110"
                        aria-label="Next design"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Image Counter */}
                  <div className="absolute top-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {activeImageIndex + 1} / {activeProjectData.images.length}
                  </div>

                  {/* Fullscreen Button */}
                  <button
                    onClick={() => openFullscreen(activeImageIndex)}
                    className="absolute top-4 right-4 bg-black/70 hover:bg-black text-white p-2 rounded-full transition-all hover:scale-110"
                    aria-label="View fullscreen"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </button>
                </div>

                {/* Thumbnail Strip */}
                <div className="mt-6 overflow-x-auto pb-4">
                  <div className="flex gap-3 min-w-max">
                    {activeProjectData.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveImageIndex(index)}
                        className={`flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${activeImageIndex === index
                          ? 'border-purple-500 scale-105 shadow-lg ring-2 ring-purple-500/30'
                          : 'border-transparent hover:border-gray-300'
                          }`}
                      >
                        <div className="w-24 h-16 bg-gray-200 dark:bg-gray-600">
                          <img
                            src={process.env.PUBLIC_URL + '/assets/' + image}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = process.env.PUBLIC_URL + '/assets/placeholder-thumb.png';
                            }}
                          />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div className="p-8 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Features */}
                  <div>
                    <h4 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
                      <span className="mr-2">✨</span> Key Features
                    </h4>
                    <ul className="space-y-3">
                      {activeProjectData.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Tools & Colors */}
                  <div>
                    <h4 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
                      <span className="mr-2">🎨</span> Design Tools
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {activeProjectData.tools.map((tool, index) => (
                        <span
                          key={index}
                          className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium"
                        >
                          {tool}
                        </span>
                      ))}
                    </div>

                    <h4 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
                      <span className="mr-2">🌈</span> Color Palette
                    </h4>
                    <div className="flex gap-2">
                      {activeProjectData.colors.map((color, index) => (
                        <div
                          key={index}
                          className="w-10 h-10 rounded-lg shadow-md"
                          style={{ backgroundColor: color }}
                          title={color}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Project Stats */}
                  <div>
                    <h4 className="text-xl font-bold text-gray-800 dark:text-white mb-4 flex items-center">
                      <span className="mr-2">📊</span> Project Stats
                    </h4>
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-4">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                          {activeProjectData.images.length}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">Design Screens</div>
                      </div>

                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                          {activeProjectData.features.length}+
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-300">User Features</div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-3">
                        <a
                          href={activeProjectData.prototypeLink}
                          className="flex-1 py-3 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all transform hover:-translate-y-1 text-center"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Prototype
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Design Philosophy */}
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-8 text-white shadow-xl">
              <div className="max-w-4xl mx-auto text-center">
                <h4 className="text-2xl font-bold mb-4">🎯 My Design Philosophy</h4>
                <p className="text-lg mb-6 opacity-90">
                  "Great design is not just about aesthetics; it's about creating intuitive experiences
                  that solve real problems while delighting users at every interaction."
                </p>
                <div className="flex flex-wrap justify-center gap-6 mt-8">
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">👁️</div>
                    <div className="font-semibold">Visual Excellence</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">🎯</div>
                    <div className="font-semibold">User-Centered</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">⚡</div>
                    <div className="font-semibold">Performance First</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold mb-2">🔄</div>
                    <div className="font-semibold">Iterative Design</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fullscreen Modal */}
      {isFullscreen && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setIsFullscreen(false)}
        >
          <button
            className="absolute top-6 right-6 text-white text-3xl hover:text-purple-400 transition-colors z-10"
            onClick={() => setIsFullscreen(false)}
            aria-label="Close fullscreen"
          >
            &times;
          </button>

          <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
            {/* Navigation Arrows */}
            {activeProjectData.images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-4 rounded-full transition-all z-10"
                  aria-label="Previous image"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-4 rounded-full transition-all z-10"
                  aria-label="Next image"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Main Image */}
            <div className="max-h-[90vh] max-w-[90vw]">
              <img
                src={process.env.PUBLIC_URL + '/assets/' + activeProjectData.images[activeImageIndex]}
                alt={`${activeProjectData.title} - Fullscreen View`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.src = process.env.PUBLIC_URL + '/assets/placeholder-ui.png';
                }}
              />
            </div>

            {/* Image Counter */}
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-lg font-medium">
              {activeImageIndex + 1} / {activeProjectData.images.length}
            </div>

            {/* Keyboard Instructions */}
            <div className="absolute top-6 left-6 text-white/70 text-sm">
              <p>Press ← → to navigate • ESC to exit</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UIUX;